import { Alert, Box, Typography } from '@mui/material'
import { t } from 'i18next'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useGetAllUnAssignedResources } from '../../../../api/task-resource'
import { CancelButton } from '../../../../components/CancelButton'
import BasicDatePicker from '../../../../components/DateTimePicker'
import { AppForm, FormSelectField, FormSubmitButton, FormTextField } from '../../../../components/FormCore'
import { TaskTypes } from '../../../../enums'
import { IResource } from '../../../../generated-types/resource'
import { ITaskResource } from '../../../../generated-types/scheduledTask'
import { useZodForm } from '../../../../hooks/zod-form'
import { getFormattedDate } from '../../../../utils'
import { dateWithoutTimeConvert } from '../../../../utils/function'
import {
  addDays,
  earliestOf,
  editableFrom,
  latestOf,
  startOfDay,
  toApiDate,
} from '../../../localities/tasks/vesselSuccession'

const clampDate = (value: Date, min: Date, max: Date) => {
  if (value < min) return min
  if (value > max) return max
  return value
}

const AssignResourceForm = ({
  initialValues,
  onSubmit,
  customerId,
  pop,
  task,
  scheduleTask,
  allowDateEntry = false,
  replaceTarget = null as ITaskResource | null,
}) => {
  const { t: translate } = useTranslation(['locality', 'common', 'task'])
  const { data: resources } = useGetAllUnAssignedResources(customerId)

  const taskStartDate = scheduleTask ? scheduleTask.startDate : task?.startDate
  const taskEndDate = scheduleTask ? scheduleTask.endDate : task?.endDate

  const replacedStart = replaceTarget?.startDate
  const replacedEnd = replaceTarget?.endDate

  const datesEnabled = allowDateEntry || task?.type == TaskTypes.Repeatable

  const earliestHandover = useMemo(() => {
    if (!replacedStart) return undefined
    const bounds = [addDays(replacedStart, 1), editableFrom()]
    return new Date(Math.max(...bounds.map(date => date.getTime())))
  }, [replacedStart])

  const latestHandover = useMemo(() => {
    if (!replacedEnd) return undefined
    const bounds = [addDays(replacedEnd, 1)]
    if (taskEndDate) bounds.push(startOfDay(taskEndDate))
    return new Date(Math.min(...bounds.map(date => date.getTime())))
  }, [replacedEnd, taskEndDate])

  const workOrderFrom = useMemo(() => (taskStartDate ? startOfDay(taskStartDate) : undefined), [taskStartDate])
  const workOrderTo = useMemo(() => (taskEndDate ? startOfDay(taskEndDate) : undefined), [taskEndDate])

  const schema = z
    .object({
      resourceId: z.number(),
      description: z.string().max(400).optional().nullable(),
      startDate: z.string().or(z.date()).optional(),
      endDate: z.string().or(z.date()).optional(),
      resource: z.object({
        id: z.number(),
      }),
      resourceDescription: z.string(),
      resourceUserId: z.string().optional().nullable(),
    })
    .refine(values => !datesEnabled || (values.startDate != null && values.startDate != ''), {
      path: ['startDate'],
      message: t('required', { ns: 'common' }),
    })
    .refine(values => !datesEnabled || (values.endDate != null && values.endDate != ''), {
      path: ['endDate'],
      message: t('required', { ns: 'common' }),
    })
    .refine(
      values =>
        !datesEnabled ||
        task?.duration == 1 ||
        (values.startDate != null &&
          values.endDate != null &&
          (allowDateEntry
            ? dateWithoutTimeConvert(values.startDate) <= dateWithoutTimeConvert(values.endDate)
            : dateWithoutTimeConvert(values.startDate) < dateWithoutTimeConvert(values.endDate))),
      { message: t('endStartDateError', { ns: 'common' }), path: ['endDate'] },
    )
    .refine(
      values =>
        !datesEnabled ||
        (values.startDate != null && dateWithoutTimeConvert(taskStartDate) <= dateWithoutTimeConvert(values.startDate)),
      { message: t('resource-start-date-range', { ns: 'task' }), path: ['startDate'] },
    )
    .refine(
      values =>
        !datesEnabled ||
        (values.endDate != null && dateWithoutTimeConvert(taskEndDate) >= dateWithoutTimeConvert(values.endDate)),
      { message: t('resource-end-date-range', { ns: 'task' }), path: ['endDate'] },
    )
    .refine(values => !replaceTarget || values.resourceId !== replaceTarget.resourceId, {
      message: t('replacement-must-differ', { ns: 'task', vessel: replaceTarget?.resource?.name ?? '' }),
      path: ['resource'],
    })
    .refine(
      values => !earliestHandover || (values.startDate != null && earliestHandover <= startOfDay(values.startDate)),
      {
        message: t('must-be-date-or-later', { ns: 'task', date: getFormattedDate(earliestHandover) }),
        path: ['startDate'],
      },
    )
    .refine(values => !latestHandover || (values.startDate != null && latestHandover >= startOfDay(values.startDate)), {
      message: t('must-be-date-or-earlier', { ns: 'task', date: getFormattedDate(latestHandover) }),
      path: ['startDate'],
    })

  const replacementDefaults = useMemo(() => {
    if (!replacedEnd || !earliestHandover || !latestHandover) return null
    const start = clampDate(startOfDay(new Date()), earliestHandover, latestHandover)
    const end = startOfDay(replacedEnd)
    return { start: toApiDate(start).toISOString(), end: toApiDate(end < start ? start : end).toISOString() }
  }, [replacedEnd, earliestHandover, latestHandover])

  const form = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      ...{
        startDate: replacementDefaults
          ? replacementDefaults.start
          : taskStartDate
          ? new Date(taskStartDate + 'Z').toISOString()
          : undefined,
        endDate: replacementDefaults
          ? replacementDefaults.end
          : taskEndDate
          ? new Date(taskEndDate + 'Z').toISOString()
          : undefined,
        ...(replaceTarget ? { description: replaceTarget.description ?? '' } : {}),
      },
    },
  })

  const watchedStart = form.watch('startDate')

  const startBounds = useMemo(
    () => ({
      min: latestOf(earliestHandover, workOrderFrom),
      max: earliestOf(latestHandover, workOrderTo),
    }),
    [earliestHandover, latestHandover, workOrderFrom, workOrderTo],
  )

  const endBounds = useMemo(
    () => ({
      min: latestOf(watchedStart ? startOfDay(watchedStart) : undefined, workOrderFrom),
      max: workOrderTo,
    }),
    [watchedStart, workOrderFrom, workOrderTo],
  )

  const handoverEnd = useMemo(() => {
    if (!replacedEnd || !watchedStart) return null
    const dayBefore = addDays(watchedStart, -1)
    return startOfDay(replacedEnd).getTime() === dayBefore.getTime() ? null : dayBefore
  }, [replacedEnd, watchedStart])

  const selectableResources = useMemo(() => {
    const all = resources?.items ?? []
    return replaceTarget ? all.filter(resource => resource.id !== replaceTarget.resourceId) : all
  }, [resources, replaceTarget])

  const onSubmitHandle = useCallback(async formValues => {
    await onSubmit(formValues, customerId, pop)
    pop()
  }, [])

  return (
    <AppForm form={form} onSubmit={onSubmitHandle} hasInitialValues={initialValues}>
      {replaceTarget && (
        <Box pb={2}>
          <Typography variant="caption" color="text.secondary">
            {translate('vessel-being-replaced', { ns: 'task' })}
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {`${replaceTarget.resource?.name ?? ''} (${getFormattedDate(replaceTarget.startDate)} - ${getFormattedDate(
              replaceTarget.endDate,
            )})`}
          </Typography>
        </Box>
      )}
      <FormSelectField
        variant="outlined"
        getOptionLabel={row => row.name}
        label={replaceTarget ? translate('replacement-vessel', { ns: 'task' }) : t('resource', { ns: 'task' })}
        data={selectableResources}
        name={'resource'}
        clearOnBlur={false}
        clearOnEscape={false}
        onChange={(evt, selectedValue) => {
          form.setValue('resource', selectedValue as IResource)
          form.setValue('resourceId', (selectedValue as IResource).id)
          form.setValue('resourceDescription', (selectedValue as IResource).description as string)
          form.setValue('resourceUserId', (selectedValue as IResource).userId as string)
        }}
      />
      {datesEnabled && (
        <>
          <BasicDatePicker
            label={translate('startDate', { ns: 'common' })}
            name="startDate"
            minDate={startBounds.min && toApiDate(startBounds.min)}
            maxDate={startBounds.max && toApiDate(startBounds.max)}
            onChange={date => {
              form.setValue('startDate', date)
              form.trigger(['startDate', 'endDate'])
            }}
          />
          <BasicDatePicker
            label={translate('endDate', { ns: 'common' })}
            name="endDate"
            minDate={endBounds.min && toApiDate(endBounds.min)}
            maxDate={endBounds.max && toApiDate(endBounds.max)}
            onChange={date => {
              form.setValue('endDate', date)
              form.trigger(['startDate', 'endDate'])
            }}
          />
        </>
      )}
      {handoverEnd && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('replaced-vessel-will-end-on', {
            ns: 'task',
            vessel: replaceTarget?.resource?.name ?? '',
            date: getFormattedDate(handoverEnd),
          })}
        </Alert>
      )}
      <FormTextField
        name="resourceDescription"
        label={translate('resource-description', { ns: 'task' })}
        multiline
        rows={3}
        inputProps={{ readOnly: true }}
        focused
      />
      <FormTextField rows={5} multiline name="description" label={translate('description', { ns: 'common' })} />
      <Box className="flex justify-end gap-4 pt-4">
        <CancelButton onClick={pop}>{translate('cancel', { ns: 'common' })}</CancelButton>
        <FormSubmitButton createText={translate(replaceTarget ? 'replace' : 'add', { ns: 'common' })} />
      </Box>
    </AppForm>
  )
}

export default AssignResourceForm
